// assets/wc50-experiments.js
/**
 * wc50-experiments.js — surface-aware experiment harness (Phase 1: reveal modal).
 *
 * PURE CORE (Node-unit-testable; no window/DOM access at module top level or in
 * these functions). Glue layer (browser-only) is added in a later task.
 *
 * Spec: docs/superpowers/specs/2026-05-22-reveal-experiment-harness-design.md
 */

import { track } from './wc77-analytics.js';

/* 2026-07-01 — VERSION D IS THE BAKED CHAMPION (flow-ui-spec §"Bake version D").
   The C/D/E reveal test concluded with D winning. The registry's fallback is now
   'version_d' and _decideVariant no longer consults the PostHog flag or the
   wc_exp_tier_* theme settings — so retiring the PostHog `reveal_experience`
   flag (or PostHog being ad-blocked entirely) can never revert a customer to
   the losing control design. renderVersionD is idempotent, so re-application
   on reopen/observer paths is a safe no-op. The other renderers remain ONLY
   for QA forcing via ?wc_exp= (control = the pre-D design). */
export const EXPERIMENTS = {
  flagKey: 'reveal_experience', // historical — no longer read for assignment
  fallback: 'version_d',        // ← the baked champion default
  variants: {
    control:    { render: (modalEl, R) => renderControl(modalEl, R) },
    tier_2card: { render: (modalEl, R) => renderTwoCardTier(modalEl, R) },
    version_d:  { render: (modalEl, R) => renderVersionD(modalEl, R) },
    // ── Letter aliases (QA / URL-override only; never PostHog flag values) ──
    // See docs/experiments/reveal-modal-variants.md for the full catalog.
    version_c:  { render: (modalEl, R) => renderControl(modalEl, R) },     // C == pre-D champion (control)
    version_b:  { render: (modalEl, R) => renderTwoCardTier(modalEl, R) }, // B (retired) 2-card
    version_e:  { render: (modalEl, R) => renderVersionE(modalEl, R) },    // E — revived B, landscape (lost to D)
    version_a:  { render: (modalEl, R) => renderVersionA(modalEl, R) },    // A (historical) — 3-tab restore
  },
};

/**
 * Decide the active variant id. Precedence: URL override → stored override
 * (sessionStorage copy of a ?wc_exp seen earlier this session — the override
 * must survive collection→PDP navigation: email links land on collections and
 * the param does not carry into product links) → PostHog flag value →
 * fallback ('control'). Any value not in the registry is ignored.
 * @param {{urlParam?:string, storedParam?:string, flagValue?:string, registry?:object}} args
 * @returns {string} a registered variant id
 */
export function resolveVariant({ urlParam, storedParam, flagValue, registry } = {}) {
  const reg = registry || EXPERIMENTS;
  const known = (v) => typeof v === 'string' && Object.prototype.hasOwnProperty.call(reg.variants, v);
  if (known(urlParam)) return urlParam;
  if (known(storedParam)) return storedParam;
  if (known(flagValue)) return flagValue;
  return reg.fallback;
}

/**
 * Return a shallow copy of the orchestrator payload with experiment fields added.
 * Never mutates the input.
 */
export function stampPayload(payload, variantId, distinctId) {
  return Object.assign({}, payload, {
    experimentVariant: variantId,
    phDistinctId: (distinctId == null ? null : String(distinctId)),
  });
}

// ─── Glue layer (browser-only) ───────────────────────────────────────────────

/** Read ?wc_exp override from the URL (QA + email-traffic variant forcing). */
function _urlOverride() {
  try { return new URL(window.location.href).searchParams.get('wc_exp') || undefined; }
  catch (e) { return undefined; }
}

/** sessionStorage key holding a ?wc_exp override seen earlier this session. */
const WC_EXP_STORE_KEY = 'wc_exp';

/** Read the persisted override (undefined if absent or storage unavailable). */
function _storedOverride() {
  try { return window.sessionStorage.getItem(WC_EXP_STORE_KEY) || undefined; }
  catch (e) { return undefined; }
}

/**
 * Persist a ?wc_exp URL override to sessionStorage so it survives navigation
 * (collection → PDP → post-upload swaps). `?wc_exp=clear` removes it. Unknown
 * values are not stored. Storage failures (private mode) degrade to the old
 * URL-only behavior. theme.liquid also runs an inline copy of this capture on
 * every page as belt-and-braces — keep the key in sync ('wc_exp').
 */
function _captureUrlOverride() {
  try {
    const raw = _urlOverride();
    if (!raw) return;
    if (raw === 'clear') { window.sessionStorage.removeItem(WC_EXP_STORE_KEY); return; }
    if (Object.prototype.hasOwnProperty.call(EXPERIMENTS.variants, raw)) {
      window.sessionStorage.setItem(WC_EXP_STORE_KEY, raw);
    }
  } catch (e) { /* non-fatal */ }
}
if (typeof window !== 'undefined') _captureUrlOverride();

/** PostHog distinct id, or null if PostHog absent. */
function _distinctId() {
  try { return (window.posthog && window.posthog.get_distinct_id) ? window.posthog.get_distinct_id() : null; }
  catch (e) { return null; }
}

/** Boot hook (called from wc77-init). Installs the modal-state observer so
 *  reopen paths render the champion (D) state.
 *  2026-07-01 — the PostHog flag-warming machinery (reloadFeatureFlags + the
 *  onFeatureFlags transient-cache-clear callback added 2026-05-26) was removed
 *  when Version D was baked as the champion default: _decideVariant no longer
 *  reads the flag, so there is nothing async to wait for and no transient
 *  'control' to heal. The observer is the only remaining boot responsibility. */
export function warmUp() {
  _ensureModalStateObserver();
}

/**
 * Decide the variant (uncached). Precedence: ?wc_exp URL override → stored
 * override → the baked champion ('version_d').
 *
 * 2026-07-01 — BAKED CHAMPION (flow-ui-spec §"Bake version D"). The previous
 * decision chain (wc_exp_tier_* theme-setting gates → PostHog `reveal_experience`
 * flag → handle allowlist) was removed on purpose: assignment must never depend
 * on a blockable third-party script or a retirable flag. With no override the
 * answer is version_d, synchronously and definitively — PostHog fully blocked
 * or the flag deleted changes nothing. The QA overrides stay above the default
 * so ?wc_exp=control / version_e etc. still force the historical renderers.
 *
 * Returns { variant, definitive } — kept as a pair for API stability with
 * getActiveVariant's caching contract; every branch is now definitive (the
 * 2026-05-26 transient-'control' machinery is unreachable but retained in
 * getActiveVariant as harmless defense).
 */
function _decideVariant() {
  const override = _urlOverride();
  if (override && Object.prototype.hasOwnProperty.call(EXPERIMENTS.variants, override)) {
    return { variant: override, definitive: true };
  }
  // Override persisted from an earlier page this session (email lands on a
  // collection with ?wc_exp; the param doesn't carry into product links).
  const stored = _storedOverride();
  if (stored && Object.prototype.hasOwnProperty.call(EXPERIMENTS.variants, stored)) {
    return { variant: stored, definitive: true };
  }
  return { variant: EXPERIMENTS.fallback, definitive: true }; // version_d — the baked champion
}

/**
 * Active variant for this visitor — decided ONCE and cached to window.WC._expVariant
 * so the reveal + ATC read the same value AFTER the URL clean-swap. Sticky-survives-
 * swap is the core lesson from the straight-on canary (a URL token got wiped).
 *
 * 2026-05-26 v2 — Only cache when _decideVariant returned a DEFINITIVE answer.
 * 2026-07-01 — with Version D baked as champion, _decideVariant is definitive
 * on every branch (override or 'version_d'), so the non-definitive path below
 * is unreachable. It is retained as harmless defense in case a future variant
 * decision reintroduces an async source; nothing clears the transient flag
 * anymore (the warmUp onFeatureFlags hook that did was removed with the
 * PostHog flag read).
 */
export function getActiveVariant() {
  try { if (window.WC && window.WC._expVariant) return window.WC._expVariant; } catch (e) {}
  const r = _decideVariant();
  if (r.definitive) {
    try {
      if (window.WC) {
        window.WC._expVariant = r.variant;
        window.WC._expVariantTransient = false;
      }
    } catch (e) {}
  } else {
    // Mark the global as not-yet-locked. Unreachable today (every
    // _decideVariant branch is definitive) — kept as defensive scaffolding.
    try { if (window.WC) window.WC._expVariantTransient = true; } catch (e) {}
  }
  return r.variant;
}

/** Stamp the orchestrator payload + return it (variant resolved here if absent). */
export function stampOrchestratorPayload(payload, variantId) {
  const v = variantId || getActiveVariant();
  return stampPayload(payload, v, _distinctId());
}

/** Fire the assignment analytics event. */
export function fireAssigned(variantId, generationId) {
  track('experiment_assigned', { reveal_experience: variantId, generation_id: generationId });
}

/**
 * Record an exposure for a generation in the caller-owned map; returns true the
 * FIRST time a given generationId is seen, false on every repeat. Pure +
 * deterministic. Why: applyToReveal can run multiple times for ONE reveal — the
 * showResult hook fires it synchronously (no flash-of-control) AND the
 * modal-state observer fires it again (covers reopen paths). Render is
 * idempotent, but the exposure event must count once per generation. A missing
 * id collapses to a single sentinel key so error states can't spam exposures.
 */
export function registerExposure(seenMap, generationId) {
  const key = generationId == null ? '__no_gen__' : String(generationId);
  if (seenMap[key]) return false;
  seenMap[key] = true;
  return true;
}

/** Per-page record of generations already counted as exposed (dedupe store). */
const _exposedGenerations = Object.create(null);

/** Fire the exposure event at render time — at most once per generationId. */
export function fireExposure(variantId, generationId) {
  if (!registerExposure(_exposedGenerations, generationId)) return;
  track('experiment_exposed', { reveal_experience: variantId, generation_id: generationId });
}

/**
 * Apply the active variant's render to the open reveal modal. Never throws —
 * any failure falls back to control (today's UI).
 * @param {HTMLElement} modalEl  the [data-wc-component="modal"] element
 * @param {object} R             window.WC._currentResult
 */
export function applyToReveal(modalEl, R) {
  const variantId = getActiveVariant();
  const entry = EXPERIMENTS.variants[variantId] || EXPERIMENTS.variants.control;
  try {
    entry.render(modalEl, R);
  } catch (e) {
    try { renderControl(modalEl, R); } catch (e2) { /* swallow */ }
  }
  fireExposure(variantId, R && R.generationId);
  return variantId;
}

/**
 * Install (once) a MutationObserver that re-applies the active variant whenever
 * the reveal modal enters the 'result' state. This is the SINGLE seam that
 * covers every way the modal reaches result:
 *   - fresh reveal (showResult's synchronous hook already runs; this re-syncs),
 *   - the "View your art" reopen pill,
 *   - State-B rehydration reopens (reopenFromStateB bubble-shortcircuit +
 *     localStorage branches) that call setModalState('result') WITHOUT
 *     re-running showResult.
 * Root cause it fixes: on a cold child-PDP load the tier was never injected in
 * that page session, so reopen showed control. Every reopen path sets
 * window.WC._currentResult BEFORE flipping modal state, so reading it here is
 * safe. Exposure is deduped per generationId (see fireExposure), so firing
 * alongside the showResult hook never double-counts.
 */
function _ensureModalStateObserver() {
  try {
    if (typeof window === 'undefined' || typeof MutationObserver === 'undefined') return;
    if (window.__wcModalStateObserver) return; // install exactly once
    var modalEl = document.querySelector('[data-wc-component="modal"]');
    if (!modalEl) {
      // Modal markup not parsed yet — retry once on the next frame.
      if (!window.__wcModalStateObserverRetry) {
        window.__wcModalStateObserverRetry = true;
        try { requestAnimationFrame(_ensureModalStateObserver); } catch (e) {}
      }
      return;
    }
    var obs = new MutationObserver(function () {
      try {
        if (modalEl.getAttribute('data-wc-modal-state') !== 'result') return;
        applyToReveal(modalEl, (window.WC && window.WC._currentResult) || null);
      } catch (e) { /* swallow — never break the modal */ }
    });
    obs.observe(modalEl, { attributes: true, attributeFilter: ['data-wc-modal-state'] });
    window.__wcModalStateObserver = obs;
  } catch (e) { /* non-fatal */ }
}

// ─── Renderers (one per experience) ──────────────────────────────────────────

/** control = today's UI. Intentional no-op: the Liquid + wc42 selectors already
 *  render the tabbed format selector. Registered so control routes identically. */
function renderControl(/* modalEl, R */) { /* no-op */ }

/** tier_2card = visible 2-card price comparison.
 *
 * Transforms the modal's native tabbed format selector into an approved
 * 2-card (Framed Canvas + Canvas Print) price-comparison UI with a Paint Kit
 * demote link beneath. Cards carry data-wc-action="select-format" so the
 * existing modal click-delegator in wc50-selectors.js handles all real
 * format switching — no format-switch logic lives here.
 *
 * Idempotent + reversible:
 *   - First call: injects styles + .wc-exp-tier, hides native tiles.
 *   - Subsequent calls: syncs selected-card state only (re-render on format change).
 *   - Rollback: remove [data-wc-tier-injected] attr + show native tiles.
 */
function _renderTierComparison(modalEl, R, opts) {
  try {
    if (!modalEl || !R) return;
    opts = opts || {};
    var variantAttr   = opts.variantAttr   || 'tier_2card';
    var canvasLabel   = opts.canvasLabel   || 'Canvas Print';
    var layout        = opts.layout        || 'portrait';
    var defaultFormat = opts.defaultFormat || null;          // e.g. 'canvas'
    var pageGuard     = opts.pageGuard      || null;          // e.g. 'canvas_or_framed_only'
    var hideDemote    = opts.hideDemote    || false;         // E: hide Paint-Kit demote link

    // Page guard: some variants only apply on canvas/framed parent pages.
    if (pageGuard === 'canvas_or_framed_only' && _isPbnParentContext()) return; // no-op → champion shows

    modalEl.setAttribute('data-wc-exp', variantAttr);
    modalEl.__wcTierCanvasLabel = canvasLabel;

    // ── 1. Inject styles once (id-guarded) ────────────────────────────────
    if (!document.getElementById('wc-exp-tier-styles')) {
      var styleEl = document.createElement('style');
      styleEl.id = 'wc-exp-tier-styles';
      styleEl.textContent = [
        '.wc-exp-tier{margin:10px 0 4px}',
        '.wc-exp-tier__cards{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin:0 0 4px}',
        '.wc-exp-tier__card{position:relative;border:2px solid #ece3dd;border-radius:13px;',
        '  padding:14px 10px 12px;text-align:center;background:#fff;cursor:pointer;',
        '  transition:border-color .15s,box-shadow .15s}',
        '.wc-exp-tier__card[data-wc-tier-selected="true"]{border-color:#5b2329;',
        '  box-shadow:0 6px 18px rgba(91,35,41,.14)}',
        '.wc-exp-tier__badge-pop{position:absolute;top:-11px;left:50%;transform:translateX(-50%);',
        '  background:#5b2329;color:#fff;font-size:10px;font-weight:800;letter-spacing:.04em;',
        '  padding:3px 9px;border-radius:999px;white-space:nowrap}',
        '.wc-exp-tier__thumb{width:86px;aspect-ratio:4/5;margin:8px auto 9px;border-radius:7px;',
        '  overflow:hidden;display:block}',
        // content-box so width:86px is the IMAGE area (same as the canvas card);
        // the 6px frame is added OUTSIDE, so both card images are identical size.
        '.wc-exp-tier__thumb--framed{background:#121212;padding:6px;box-sizing:content-box}',
        '.wc-exp-tier__thumb--framed img{display:block;width:100%;height:100%;',
        '  object-fit:cover}',
        '.wc-exp-tier__thumb--canvas img{display:block;width:100%;height:100%;',
        '  object-fit:cover;box-shadow:0 4px 11px rgba(0,0,0,.2)}',
        '.wc-exp-tier__name{font-weight:800;font-size:13px;margin:2px 0 4px;color:#2b1a17}',
        '.wc-exp-tier__price{display:flex;gap:6px;align-items:baseline;',
        '  justify-content:center;flex-wrap:wrap}',
        '.wc-exp-tier__compare{color:#7c6f6a;text-decoration:line-through;font-size:12px}',
        '.wc-exp-tier__sale{font-weight:800;font-size:17px;color:#2b1a17}',
        '.wc-exp-tier__save{display:inline-block;margin-top:5px;background:#e23b6d;',
        '  color:#fff;font-size:10px;font-weight:800;padding:1px 7px;border-radius:999px}',
        '.wc-exp-tier__demote{display:block;text-align:center;color:#7c6f6a;',
        '  font-size:12.5px;margin:12px 0 2px;text-decoration:underline;cursor:pointer;',
        '  background:none;border:none;width:100%;padding:0}',
        // ── Landscape modifier (Version E): two cards SIDE-BY-SIDE on one row,
        //    each card image-left / text-right. Image + fonts shrunk so both
        //    landscape cards fit the format column on a single row.
        '.wc-exp-tier--landscape .wc-exp-tier__cards{grid-template-columns:1fr 1fr;gap:8px}',
        // Both cards: bottom padding matches so the pair sits balanced; the
        // "MOST POPULAR" card gets EXTRA top padding (below) to give the
        // overhanging badge clear breathing room above the content.
        '.wc-exp-tier--landscape .wc-exp-tier__card{display:flex;flex-direction:row;',
        '  align-items:center;gap:9px;text-align:left;padding:11px 10px 13px}',
        '.wc-exp-tier--landscape .wc-exp-tier__card:has(.wc-exp-tier__badge-pop){padding-top:23px}',
        '.wc-exp-tier--landscape .wc-exp-tier__thumb{margin:0;flex:0 0 auto;width:50px}',
        '.wc-exp-tier--landscape .wc-exp-tier__thumb--framed{width:44px}',
        '.wc-exp-tier--landscape .wc-exp-tier__body{flex:1 1 auto;min-width:0}',
        '.wc-exp-tier--landscape .wc-exp-tier__name{margin:0 0 3px;font-size:12.5px}',
        '.wc-exp-tier--landscape .wc-exp-tier__price{justify-content:flex-start}',
        '.wc-exp-tier--landscape .wc-exp-tier__sale{font-size:15px}',
        '.wc-exp-tier--landscape .wc-exp-tier__save{margin-top:4px}',
        '.wc-exp-tier--landscape .wc-exp-tier__badge-pop{top:-10px;left:10px;transform:none}',
        // ── Mobile (<750px): the name heading gets its OWN row ABOVE the image+price
        //    so the narrow card is balanced. Card becomes a grid; _placeTierNames()
        //    moves the name out of the body to a direct child so grid-area placement
        //    works. Desktop (flex-row, name inside body) is left untouched.
        '@media (max-width:750px){',
        // Both cards share the SAME top space (22px) so the popular card's badge
        // doesn't push its name/image/price lower than the other — names, images,
        // prices and SAVE pills line up across the pair (uniform).
        '  .wc-exp-tier--landscape .wc-exp-tier__card{display:grid;grid-template-columns:auto 1fr;',
        '    column-gap:10px;row-gap:7px;align-items:center;grid-template-areas:"name name" "thumb body";padding-top:22px}',
        '  .wc-exp-tier--landscape .wc-exp-tier__card:has(.wc-exp-tier__badge-pop){padding-top:22px}',
        '  .wc-exp-tier--landscape .wc-exp-tier__thumb{grid-area:thumb;margin:0;align-self:center}',
        // Shrink the framed image so its OUTER size (image + 6px frame each side)
        // equals the bare canvas thumb — identical footprints + equal price columns
        // (keeps "SAVE 20%" on one line in BOTH cards).
        '  .wc-exp-tier--landscape .wc-exp-tier__thumb--framed{width:38px}',
        '  .wc-exp-tier--landscape .wc-exp-tier__name{grid-area:name;align-self:start;margin:0;font-size:13px}',
        '  .wc-exp-tier--landscape .wc-exp-tier__body{grid-area:body;align-self:center;min-width:0}',
        '  .wc-exp-tier--landscape .wc-exp-tier__save{white-space:nowrap}',
        '}',
      ].join('');
      document.head.appendChild(styleEl);
    }

    // ── 2. Locate native tiles container ──────────────────────────────────
    var tilesEl = modalEl.querySelector('.wolden-customize__format-tiles');
    if (!tilesEl) return; // unexpected DOM — bail safely

    // ── 3. Inject .wc-exp-tier once; subsequent calls just re-sync state ──
    var alreadyInjected = !!modalEl.getAttribute('data-wc-tier-injected');

    if (!alreadyInjected) {
      // Hide native tiles (reversible: display='' restores)
      tilesEl.style.display = 'none';

      // Also hide the card-label "Choose Your Format" above tiles if present,
      // since we preserve "Choose your format" as the existing h2/label above.
      var cardLabelEl = tilesEl.previousElementSibling;
      if (cardLabelEl && cardLabelEl.classList.contains('wolden-customize__card-label')) {
        cardLabelEl.setAttribute('data-wc-tier-hid-label', 'true');
        cardLabelEl.style.display = 'none';
      }

      // Build the tier container
      var tier = document.createElement('div');
      tier.className = 'wc-exp-tier' + (layout === 'landscape' ? ' wc-exp-tier--landscape' : '');

      // 2-card grid
      var cardsDiv = document.createElement('div');
      cardsDiv.className = 'wc-exp-tier__cards';

      cardsDiv.appendChild(_buildTierCard('canvas_framed', R, { canvasLabel: canvasLabel }));
      cardsDiv.appendChild(_buildTierCard('canvas', R, { canvasLabel: canvasLabel }));
      tier.appendChild(cardsDiv);

      // Paint Kit demote link — Version E hides it (E tests Framed vs Wrapped only).
      if (!hideDemote) {
        var demote = document.createElement('button');
        demote.className = 'wc-exp-tier__demote';
        demote.setAttribute('data-wc-action', 'select-format');
        demote.setAttribute('data-wc-format', 'paint_kit');
        demote.setAttribute('type', 'button');
        demote.textContent = 'or DIY Paint By Numbers Kit - $49.97 ›';
        tier.appendChild(demote);
      }

      // Insert right after the (now-hidden) tiles
      tilesEl.parentNode.insertBefore(tier, tilesEl.nextSibling);
      modalEl.setAttribute('data-wc-tier-injected', 'true');

      // Variant default-format override (e.g. Version E pins Wrapped Canvas for
      // PDP price-continuity). First render only — never override a later choice.
      if (defaultFormat && !modalEl.__wcTierDefaulted) {
        modalEl.__wcTierDefaulted = true;
        _forceTierDefaultFormat(modalEl, R, defaultFormat);
      }

      // Light click listener: sync selected-card styling when a card is clicked.
      // The delegator in wc50-selectors.js does the real format switch; we just
      // update visual state here so it feels instant.
      tier.addEventListener('click', function(e) {
        try {
          var btn = e.target && e.target.closest('[data-wc-action="select-format"]');
          if (!btn) return;
          var fmt = btn.getAttribute('data-wc-format');
          if (fmt === 'canvas_framed' || fmt === 'canvas') {
            _syncTierSelected(tier, fmt);
          }
          // paint_kit clicks flow through to delegator — no visual sync needed here
        } catch (ex) { /* swallow */ }
      });

      // Re-sync card selected-state to R.activeFormat on EVERY format change
      // (cards, the paint-kit demote link, or any external change). The click
      // listener above is only instant feedback for card clicks; this catches
      // paint_kit selection (clears both cards) + any drift. format_toggled fires
      // AFTER R.activeFormat is updated, so reading R here is correct.
      document.addEventListener('wolden:track', function (ev) {
        try {
          var en = ev && ev.detail && ev.detail.eventName;
          if (en !== 'format_toggled' && en !== 'size_selected') return;
          var RR = (window.WC && window.WC._currentResult) || {};
          if (en === 'format_toggled') {
            var t = modalEl.querySelector('.wc-exp-tier');
            if (t) _syncTierSelected(t, RR.activeFormat || 'canvas');
            _syncTierHeadline(modalEl);
          }
          // Both events: keep the active card's price in sync with the selected size.
          _syncTierCardPrices(modalEl, RR);
        } catch (e) { /* swallow */ }
      });

      // Headline: the LEFT eyebrow becomes the dynamic format-name (Premium
      // Framed Canvas / Superior Pre-Printed Canvas / Paint By Numbers Kit),
      // styled exactly like the native branch-header; the native (right-column)
      // branch-headers are hidden, which also lifts the Size row up. Kept in
      // sync on every format change (listener above) + every render (section 4).
      _syncTierHeadline(modalEl);
    }

    // ── 4. Sync selected-card state (runs every call for re-renders) ──────
    var existingTier = modalEl.querySelector('.wc-exp-tier');
    if (existingTier) {
      var activeFormat = (R && R.activeFormat) || 'canvas';
      _syncTierSelected(existingTier, activeFormat);
      _syncTierHeadline(modalEl);
      // Normalize card prices: active card → selected size, inactive → 8x10.
      _syncTierCardPrices(modalEl, R);
    }

    // ── 4b. Mobile (<750px): lift each card's NAME heading above the image+price
    //    (landscape/Version E only). Desktop keeps the name in the body.
    _placeTierNames(modalEl);
    _wireTierNameMQ(modalEl);

    // ── 5. Update card images if mockups resolved since first inject ───────
    if (alreadyInjected) {
      _refreshTierImages(modalEl, R);
    }

  } catch (e) {
    /* defensive: never throw — applyToReveal's catch falls back to control */
  }
}

/**
 * Mobile (<750px): move each landscape card's NAME heading out of the body to be
 * a direct child of the card, so the mobile CSS grid (grid-area "name") places it
 * on its own row ABOVE the image+price. On desktop the name stays inside the body
 * (flex-row layout, unchanged). Idempotent — safe to call on every render.
 */
function _placeTierNames(modalEl) {
  try {
    if (!modalEl) return;
    var mobile = window.matchMedia('(max-width:750px)').matches;
    var cards = modalEl.querySelectorAll('.wc-exp-tier--landscape .wc-exp-tier__card');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var name = card.querySelector('.wc-exp-tier__name');
      var body = card.querySelector('.wc-exp-tier__body');
      var thumb = card.querySelector('.wc-exp-tier__thumb');
      if (!name || !body || !thumb) continue;
      if (mobile) {
        if (name.parentElement !== card) card.insertBefore(name, thumb);   // name → card top
      } else {
        if (name.parentElement !== body) body.insertBefore(name, body.firstChild); // name → back in body
      }
    }
  } catch (e) { /* swallow */ }
}

/** Re-run _placeTierNames when the 750px breakpoint is crossed (resize/rotate). Wired once per modal. */
function _wireTierNameMQ(modalEl) {
  try {
    if (!modalEl || modalEl.__wcTierNameMQ) return;
    modalEl.__wcTierNameMQ = true;
    var mq = window.matchMedia('(max-width:750px)');
    var handler = function () { _placeTierNames(modalEl); };
    if (mq.addEventListener) mq.addEventListener('change', handler);
    else if (mq.addListener) mq.addListener(handler);
  } catch (e) { /* swallow */ }
}

/** Version B — the original 2-card comparison (portrait cards). */
function renderTwoCardTier(modalEl, R) {
  return _renderTierComparison(modalEl, R, {
    variantAttr: 'tier_2card',
    canvasLabel: 'Canvas Print',
    layout:      'portrait',
  });
}

/** Version E — Version B's 2-card comparison, revived: landscape cards, "Wrapped
 *  Canvas" label, Wrapped-Canvas default (PDP price-continuity), canvas/framed pages
 *  only. See docs/experiments/reveal-modal-variants.md. */
function renderVersionE(modalEl, R) {
  return _renderTierComparison(modalEl, R, {
    variantAttr:   'version_e',
    canvasLabel:   'Wrapped Canvas',
    layout:        'landscape',
    defaultFormat: 'canvas',
    pageGuard:     'canvas_or_framed_only',
    hideDemote:    true,
  });
}

/** Version A (historical) — restore the 3-tab format strip (Framed Canvas / Canvas
 *  Print / Paint Kit). The base modal currently renders only Canvas + Paint Kit;
 *  A re-injects the Framed tile. QA/URL only — not a live flag value.
 *  See docs/experiments/reveal-modal-variants.md. */
function renderVersionA(modalEl, R) {
  try {
    if (!modalEl) return;
    modalEl.setAttribute('data-wc-exp', 'version_a');
    var tiles = modalEl.querySelector('.wolden-customize__format-tiles');
    if (!tiles) return;
    if (modalEl.querySelector('.wolden-customize__format-tile[data-wc-format="canvas_framed"]')) return; // idempotent
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'wolden-customize__format-tile';
    btn.setAttribute('data-wc-action', 'select-format');
    btn.setAttribute('data-wc-format', 'canvas_framed');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', 'false');
    btn.innerHTML =
      '<span class="wolden-customize__format-badge wolden-customize__format-badge--premium">PREMIUM</span>' +
      '<span class="wolden-customize__format-icon" aria-hidden="true">' +
      '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="18" rx="1.5" ry="1.5"/><rect x="5" y="6" width="14" height="12" rx="0.5" ry="0.5"/><circle cx="9" cy="10" r="1.2"/><path d="m19 16-3.086-3.086a2 2 0 0 0-2.828 0L7 19"/></svg>' +
      '</span>' +
      '<span class="wolden-customize__format-name">Framed Canvas</span>';
    tiles.insertBefore(btn, tiles.firstChild); // Framed first, matching the original A order
  } catch (e) { /* swallow — never break the modal */ }
}

// ─── Tier helpers (private, not exported) ────────────────────────────────────

/**
 * Canonical tier price tables — the single source of truth for both cards.
 * 2026-05-29 — framed 8x10 compare 94.97→93.97 so the card reads SAVE 20% (matches
 * Wrapped); the product's compare-at is also set in the framed-mockup Lambda.
 * 2026-05-26 — framed 24x30 set to 159.97/186.97 (was 179.97/224.97).
 * Exported so _tierPricingFor can be unit-tested.
 */
export const TIER_PRICING = {
  canvas_framed: {
    '8x10':  { sale: 74.97,  compare: 93.97 },
    '16x20': { sale: 114.97, compare: 144.97 },
    '24x30': { sale: 159.97, compare: 186.97 },
  },
  canvas: {
    '8x10':  { sale: 47.90, compare: 59.97 },
    '16x20': { sale: 79.97, compare: 99.97 },
    '24x30': { sale: 119.97, compare: 149.97 },
  },
};

/**
 * Price for a tier card. The ACTIVE format's card reflects the customer's selected
 * size; every other (inactive) card stays at the base 8x10 price. Pure + exported
 * so it is unit-testable.
 * @param {'canvas_framed'|'canvas'} format  which card
 * @param {object} R  window.WC._currentResult (activeFormat / activeCanvasSize / activeCanvasFramedSize)
 * @returns {{sale:number, compare:number}}
 */
export function _tierPricingFor(format, R) {
  var isFramed = format === 'canvas_framed';
  var map = isFramed ? TIER_PRICING.canvas_framed : TIER_PRICING.canvas;
  var activeFormat = (R && R.activeFormat) || 'canvas';
  var sizeKey = '8x10';
  if (format === activeFormat) {
    sizeKey = (isFramed ? (R && R.activeCanvasFramedSize) : (R && R.activeCanvasSize)) || '8x10';
  }
  return map[sizeKey] || map['8x10'];
}

/**
 * Build a single format card element.
 * @param {'canvas_framed'|'canvas'} format
 * @param {object} R  window.WC._currentResult
 */
function _buildTierCard(format, R, opts) {
  var isFramed = format === 'canvas_framed';
  var canvasLabel = (opts && opts.canvasLabel) || 'Canvas Print';
  var displayName = isFramed ? 'Framed Canvas' : canvasLabel;

  // Pricing — single source of truth: TIER_PRICING + _tierPricingFor (unit-tested).
  // The ACTIVE format's card reflects the selected size; the inactive card stays 8x10.
  var pricing = _tierPricingFor(format, R);

  // Image URL — prefer the appropriate mockup, fall back to artDisplayUrl
  var imgUrl = _pickTierImage(format, R);

  // Savings badge text
  var pct = (pricing.compare > pricing.sale)
    ? Math.round(((pricing.compare - pricing.sale) / pricing.compare) * 100)
    : 0;
  var saveText = pct ? 'SAVE ' + pct + '%' : '';

  // Card element
  var card = document.createElement('div');
  card.className = 'wc-exp-tier__card';
  card.setAttribute('data-wc-action', 'select-format');
  card.setAttribute('data-wc-format', format);
  card.setAttribute('data-wc-tier-card', format);
  card.setAttribute('role', 'button');
  card.setAttribute('tabindex', '0');
  card.setAttribute('aria-label', displayName + ' $' + pricing.sale.toFixed(2));

  // MOST POPULAR badge (framed only)
  if (isFramed) {
    var pop = document.createElement('div');
    pop.className = 'wc-exp-tier__badge-pop';
    pop.textContent = '★ MOST POPULAR';
    card.appendChild(pop);
  }

  // Thumbnail
  var thumbWrap = document.createElement('div');
  thumbWrap.className = 'wc-exp-tier__thumb ' +
    (isFramed ? 'wc-exp-tier__thumb--framed' : 'wc-exp-tier__thumb--canvas');
  thumbWrap.setAttribute('data-wc-tier-thumb', format);

  var img = document.createElement('img');
  img.src = imgUrl || '';
  img.alt = '';
  img.setAttribute('loading', 'lazy');
  img.style.display = imgUrl ? '' : 'none';
  thumbWrap.appendChild(img);
  card.appendChild(thumbWrap);

  // Text body (name + price + save) — grouped so landscape can place it right of the image.
  var body = document.createElement('div');
  body.className = 'wc-exp-tier__body';

  // Format name
  var name = document.createElement('div');
  name.className = 'wc-exp-tier__name';
  name.textContent = displayName;
  body.appendChild(name);

  // Price row
  var priceDiv = document.createElement('div');
  priceDiv.className = 'wc-exp-tier__price';
  var compareSpan = document.createElement('span');
  compareSpan.className = 'wc-exp-tier__compare';
  compareSpan.textContent = '$' + pricing.compare.toFixed(2);
  var saleSpan = document.createElement('span');
  saleSpan.className = 'wc-exp-tier__sale';
  saleSpan.textContent = '$' + pricing.sale.toFixed(2);
  priceDiv.appendChild(compareSpan);
  priceDiv.appendChild(saleSpan);
  body.appendChild(priceDiv);

  // SAVE badge
  if (saveText) {
    var saveBadge = document.createElement('span');
    saveBadge.className = 'wc-exp-tier__save';
    saveBadge.textContent = saveText;
    body.appendChild(saveBadge);
  }

  card.appendChild(body);

  return card;
}

/**
 * Make the LEFT result-card eyebrow the page headline showing the active
 * format's name (Premium Framed Canvas / Superior Pre-Printed Canvas / Paint By
 * Numbers Kit) with the EXACT native branch-header styling, and hide the native
 * (right-column) branch-headers so the name shows once and the Size row lifts up.
 * Idempotent; re-runs on every render + format change.
 */
function _syncTierHeadline(modalEl) {
  try {
    if (!modalEl) return;
    // First call: find by native class. Later calls: by our marker class (we swap
    // the class so it inherits branch-header styling, so card-label no longer matches).
    var eyebrow = modalEl.querySelector('.wc-exp-headline')
      || modalEl.querySelector('.wolden-customize__result-art-card .wolden-customize__card-label');
    if (!eyebrow) return;
    if (!eyebrow.classList.contains('wc-exp-headline')) {
      eyebrow.classList.remove('wolden-customize__card-label');
      eyebrow.classList.add('wolden-customize__branch-header', 'wc-exp-headline');
    }
    // Hide the native (right-column) branch-headers (they live inside
    // [data-wc-format-branch]; our eyebrow does not, so it is unaffected).
    var heads = modalEl.querySelectorAll('[data-wc-format-branch] .wolden-customize__branch-header');
    for (var i = 0; i < heads.length; i++) heads[i].style.display = 'none';
    // Also hide the LEFT result-card format card-labels (e.g. "Premium Framed Canvas")
    // so the gallery image sits at the SAME Y for every format in the tier comparison
    // (the selected card already names the format). Scoped to B/E — Version D untouched.
    var galLabels = modalEl.querySelectorAll('.wolden-customize__result-art-card .wolden-customize__card-label');
    for (var g = 0; g < galLabels.length; g++) galLabels[g].style.display = 'none';
    // Mirror the active format's name (read the native header text for fidelity).
    var R = (window.WC && window.WC._currentResult) || {};
    var fmt = R.activeFormat || 'canvas';
    var src = modalEl.querySelector('[data-wc-format-branch="' + fmt + '"] .wolden-customize__branch-header');
    var name = (src && src.textContent.trim())
      || (fmt === 'canvas' ? 'Superior Pre-Printed Canvas'
         : fmt === 'paint_kit' ? 'Paint By Numbers Kit'
         : 'Premium Framed Canvas');
    // Variant canvas-label override (Version E): when a NON-default canvas label is
    // active (e.g. "Wrapped Canvas"), the eyebrow mirrors the selected card's name so
    // headline and card agree. Version B keeps the default 'Canvas Print' label, so its
    // eyebrow stays the native marketing phrase ("Superior Pre-Printed Canvas").
    var canvasLabelOverride = modalEl.__wcTierCanvasLabel;
    if (fmt === 'canvas' && canvasLabelOverride && canvasLabelOverride !== 'Canvas Print') {
      name = canvasLabelOverride;
    }
    if (eyebrow.textContent !== name) eyebrow.textContent = name;
  } catch (e) { /* swallow */ }
}

/**
 * Image for a tier card. BOTH cards show the plain main art (R.artDisplayUrl) —
 * NOT the small format mockups. The framed card's border treatment
 * (.wc-exp-tier__thumb--framed) communicates "framed"; the canvas card shows the
 * bare art. Plain art reads far clearer at thumbnail size than tiny mockups.
 */
function _pickTierImage(format, R) {
  if (!R) return '';
  return R.artDisplayUrl || '';
}

/** Mark the card matching activeFormat as selected; deselect the other. */
function _syncTierSelected(tierEl, activeFormat) {
  try {
    var cards = tierEl.querySelectorAll('[data-wc-tier-card]');
    for (var i = 0; i < cards.length; i++) {
      var fmt = cards[i].getAttribute('data-wc-tier-card');
      var selected = fmt === activeFormat;
      cards[i].setAttribute('data-wc-tier-selected', selected ? 'true' : 'false');
    }
  } catch (e) { /* swallow */ }
}

/**
 * Repaint each tier card's compare/sale price + SAVE badge from _tierPricingFor:
 * the ACTIVE format's card shows the customer's selected size, every other card
 * shows the base 8x10. Idempotent — safe to call on every size/format change and
 * on every render. Never throws.
 */
function _syncTierCardPrices(modalEl, R) {
  try {
    if (!modalEl) return;
    var tier = modalEl.querySelector('.wc-exp-tier');
    if (!tier) return;
    var cards = tier.querySelectorAll('[data-wc-tier-card]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var fmt = card.getAttribute('data-wc-tier-card');
      if (fmt !== 'canvas_framed' && fmt !== 'canvas') continue; // paint_kit has no price card
      var p = _tierPricingFor(fmt, R);
      var cmp = card.querySelector('.wc-exp-tier__compare');
      if (cmp) cmp.textContent = '$' + p.compare.toFixed(2);
      var sale = card.querySelector('.wc-exp-tier__sale');
      if (sale) sale.textContent = '$' + p.sale.toFixed(2);
      var save = card.querySelector('.wc-exp-tier__save');
      if (save) {
        var pct = (p.compare > p.sale) ? Math.round(((p.compare - p.sale) / p.compare) * 100) : 0;
        if (pct > 0) { save.textContent = 'SAVE ' + pct + '%'; save.style.display = ''; }
        else { save.style.display = 'none'; }
      }
      var nameEl = card.querySelector('.wc-exp-tier__name');
      var nm = (nameEl && nameEl.textContent) ? nameEl.textContent.trim() : '';
      card.setAttribute('aria-label', (nm ? nm + ' ' : '') + '$' + p.sale.toFixed(2));
    }
  } catch (e) { /* swallow — never break the modal */ }
}

/** Refresh card images after mockups resolve (called on re-renders). */
function _refreshTierImages(modalEl, R) {
  try {
    var thumbs = modalEl.querySelectorAll('[data-wc-tier-thumb]');
    for (var i = 0; i < thumbs.length; i++) {
      var fmt = thumbs[i].getAttribute('data-wc-tier-thumb');
      var url = _pickTierImage(fmt, R);
      var img = thumbs[i].querySelector('img');
      if (img && url && img.src !== url) {
        img.src = url;
        img.style.display = '';
      }
    }
  } catch (e) { /* swallow */ }
}

/** Force the tier's active format (first render only) + repaint gallery/price/cards. */
function _forceTierDefaultFormat(modalEl, R, fmt) {
  try {
    if (!R || R.activeFormat === fmt) { _syncAfterTierDefault(modalEl, R); return; }
    R.activeFormat = fmt;
    _syncAfterTierDefault(modalEl, R);
  } catch (e) { /* swallow — never break the modal */ }
}

/** Run the canonical refresh chain so gallery + price + selected-card reflect R.activeFormat. */
function _syncAfterTierDefault(modalEl, R) {
  var wc = (typeof window !== 'undefined' && window.WC) ? window.WC : {};
  try { if (typeof wc.refreshActiveBranches === 'function') wc.refreshActiveBranches(); } catch (e) {}
  try { if (typeof wc.refreshSizeFramePills === 'function') wc.refreshSizeFramePills(); } catch (e) {}
  try { if (typeof wc.refreshMockupImages   === 'function') wc.refreshMockupImages();   } catch (e) {}
  try { if (typeof wc.refreshPricingDisplay === 'function') wc.refreshPricingDisplay(); } catch (e) {}
  try { if (typeof wc.updatePdpVariantState === 'function') wc.updatePdpVariantState(); } catch (e) {}
  try { if (typeof wc.renderUnifiedGallery  === 'function') wc.renderUnifiedGallery();  } catch (e) {}
  try {
    var t = modalEl.querySelector('.wc-exp-tier');
    if (t) _syncTierSelected(t, (R && R.activeFormat) || 'canvas');
    _syncTierHeadline(modalEl);
  } catch (e) {}
}

/** True when the customer's PARENT product is a Paint-Kit (PBN) product. Keys off the
 *  parent identity captured at upload (survives the child-page navigation), with the
 *  parent-page body class as a fallback. Unknown → false (render; the funnel is
 *  canvas-dominant). */
function _isPbnParentContext() {
  try {
    var R = (typeof window !== 'undefined' && window.WC && window.WC._currentResult) || {};
    var hay = [
      (window.WC && window.WC._parentHandle) || '',
      R.productType || '', R.productHandle || '', R.parentHandle || ''
    ].join(' ').toLowerCase();
    if (hay.indexOf('paint') !== -1 || hay.indexOf('pbn') !== -1 || hay.indexOf('numbers') !== -1) return true;
    if (hay.indexOf('canvas') !== -1) return false;
    if (document.querySelector('.wc-canvas-parent')) return false; // parent page is canvas/framed
    return false;
  } catch (e) { return false; }
}

/* ─────────────────────────────────────────────────────────────────────────
   Version D — Canvas-with-nested-Framed re-organization (2026-05-25)

   ★ 2026-07-01 — BAKED AS THE CHAMPION DEFAULT (flow-ui-spec §"Bake version D").
   D won the C/D/E reveal test. Everything below is now the DEFAULT reveal
   experience: _decideVariant resolves 'version_d' for every customer without
   consulting PostHog, so the champion render path (wc77-result.js showResult →
   applyToReveal, the modal-state observer, and the PDP-options bootstrap at
   the bottom of this file) applies these mutations unconditionally. The code
   stays in this module (rather than being rewritten into the Liquid) because
   the D experience is behavioral — injection is idempotent/self-healing by
   design and the delegator/state-sync logic must run either way. Only a QA
   ?wc_exp override can render anything else.

   Spec: docs/superpowers/specs/2026-05-25-ab-testing-operating-system-design.md
   Test row: wolden-ops/data/funnel-experiments.json (modal-v4-canvas-nested-framed)

   IDEA: "Framed Canvas" and "Canvas Print" are both canvas — one's just the
   framed variant of the other (like Paint Kit's Frame upgrade). So fold the
   Framed choice UNDER the Canvas Print panel as a sub-row, and add a sized
   "Add a frame" upsell card. Same Shopify variants, same gallery images,
   same ATC behavior — purely a re-organization of WHERE the controls live.

   MECHANIC: the "Framed: Frame" toggle silently sets R.activeFormat to
   'canvas_framed'; "No Frame" sets it back to 'canvas'. The existing
   gallery + branch + ATC machinery (driven by activeFormat) does the rest.
   We override the RIGHT-column branch visibility so the Canvas panel stays
   visible while activeFormat flips between canvas and canvas_framed under
   the hood. The LEFT slider deck still toggles canvas vs canvas_framed
   normally so the gallery shows the right mockup.
   ───────────────────────────────────────────────────────────────────────── */

/* 2026-07-02 QA round-3 — the old WC_D_UPSELL table hardcoded rounded
   marketing numbers ("$50 → $30") that did NOT match the real per-size cost
   of adding the frame. The card now computes the HONEST delta from the SAME
   price tables the price line uses (CANVAS_PRICING / CANVAS_FRAMED_PRICING,
   attached to window.WC by wc77-selectors.js — the established no-circular-
   import channel between these two modules):
     sale delta   = framed sale price   − canvas sale price   (what adding
                    the frame actually costs the customer today)
     strike delta = framed compare-at   − canvas compare-at   (what it would
                    cost at compare-at prices)
   The strike + SAVE badge only render when the strike delta is genuinely
   larger than the sale delta — a size where it isn't (24x30) shows the
   plain honest price with no struck number and no badge. No static claims
   anywhere: if the tables aren't attached yet, the card shows NO numbers
   rather than made-up ones. */
function _wcDComputeUpsellDelta(size) {
  try {
    var wc = (typeof window !== 'undefined' && window.WC) ? window.WC : {};
    var cp = wc.CANVAS_PRICING && wc.CANVAS_PRICING[size];
    var fp = wc.CANVAS_FRAMED_PRICING && wc.CANVAS_FRAMED_PRICING[size];
    if (!cp || !fp || typeof cp.sale !== 'number' || typeof fp.sale !== 'number') return null;
    var sale = fp.sale - cp.sale;
    if (!(sale > 0)) return null;
    var strike = (typeof cp.compare === 'number' && typeof fp.compare === 'number')
      ? (fp.compare - cp.compare) : null;
    if (!(strike > sale)) strike = null; // never strike a number ≤ the real price
    var pct = strike ? Math.round(((strike - sale) / strike) * 100) : null;
    if (pct !== null && pct < 1) { strike = null; pct = null; }
    return { sale: sale, strike: strike, pct: pct };
  } catch (e) { return null; }
}

/* "$27.07" / "$35" — exact cents when they exist, clean dollars when not. */
function _wcDFmtDelta(n) {
  if (typeof n !== 'number' || !isFinite(n)) return '';
  var r = Math.round(n * 100) / 100;
  return (r % 1 === 0) ? ('$' + r.toFixed(0)) : ('$' + r.toFixed(2));
}

const WC_D_STYLE_TAG_ID = 'wc-version-d-styles';

/* 2026-05-25 v3 — Three small rules in this <style> tag:

   1) Fieldset spacing on the canvas branch (mirrors wolden-customize.css:3358-3361
      which originally excluded canvas because it only had one fieldset). Without
      this, the injected Frame fieldset gets the browser-default fieldset margin
      and visibly "floats" above the Paint-Kit-matching 2px gap.

   2) <button> reset for the upsell. Our upsell mirrors the PBN DIY-Frame upsell
      class structure but uses a <button> (single-action) rather than the PBN's
      <label>+checkbox (bidirectional toggle). The <button> brings default UA
      styling (border/text-align/font) that fights the .wolden-customize__diy-
      frame-upsell rules, so we reset those bits — and only those bits — back
      to inherited/default.

   3) Emoji sizing on the upsell image chip. The DIY Frame upsell's .image
      slot is 44×44 and contains a 32-40px <img>. We swap in an emoji, which
      needs explicit font-size to fill the same space (no img to size against).

   Scoping every selector to [data-wc-exp="version_d"] keeps non-VD customers
   bit-identical to today. */
const WC_D_STYLES =
  '[data-wc-exp="version_d"] [data-wc-format-branch="canvas"] fieldset + fieldset { margin-top: 2px; }\n' +
  '[data-wc-exp="version_d"] .wc-d-upsell-image { font-size: 32px; line-height: 1; }\n' +
  /* 2026-07-02 (upsell parity) — on-page context adaptation for the flow
     reveal surfaces: the card sits at the end of the canvas branch (below
     the Frame / Frame Color fieldsets) instead of the modal's price-line/ATC
     slot, so it needs its own breathing room above; the modal's base rule
     (wolden-customize.css) supplies the bottom margin. Scoped to the flow
     roots so the modal card is bit-identical to before. */
  '[data-wc-component="flow-b"] [data-wc-d-upsell], [data-wc-component="flow-c"] [data-wc-d-upsell] { margin-top: 10px; }\n' +
  '';

function _wcDEnsureStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(WC_D_STYLE_TAG_ID)) return;
  var style = document.createElement('style');
  style.id = WC_D_STYLE_TAG_ID;
  style.textContent = WC_D_STYLES;
  document.head.appendChild(style);
}

/* 2026-05-25 v2 — Emits a Frame ("No Frame" / "Frame") fieldset that's
   structurally + class-wise IDENTICAL to snippets/wolden-customize-modal.liquid:390-430
   (Paint Kit "Frame :" picker). Two options: "No Frame" (default) and "Frame".
   Active state driven by window.WC._setSelected (the same helper that paints the
   existing pills). The name attribute is parameterised so we can re-use the
   builder for the modal AND the pdp-options surface without radio collisions. */
function _wcDBuildFrameRow(nameAttr) {
  return (
    '<fieldset class="variant-option variant-option--buttons variant-option--equal-width-buttons" data-option-name="Frame" data-wc-d-row="frame">' +
      '<legend class="gen:w-full">' +
        '<div class="gen:flex gen:justify-between gen:items-center">' +
          '<div class="gen:flex gen:gap-1.5 gen:items-center">' +
            '<span class="variant-level-customization">Frame :</span>' +
            '<span class="variant-option__swatch-value variant-level-value-customization gen:leading-[26px]" data-selected-text-target data-wc-d-frame-display>No Frame</span>' +
          '</div>' +
        '</div>' +
      '</legend>' +
      '<div class="gen:grid">' +
        '<div class="gen:min-w-0 gen:relative">' +
          '<div class="swiper variant-swiper variant-swiper-frame">' +
            '<div class="pro_var">' +
              '<div class="swiper-slide gen:!w-auto">' +
                '<label class="variant-option__button-label variant-option__button-label--no-swatch  gen:!border-[2px]  gen:!py-[11px] gen:min-[560px]:!py-[10px] gen:!px-[12px] ">' +
                  '<input type="radio" name="' + nameAttr + '" value="No Frame" aria-label="No Frame" data-wc-action="select-canvas-frame-mode-d" data-wc-frame-mode="no-frame" checked>' +
                  '<span class="variant-option__button-label__text gen:min-[560px]:!text-[15px]">No Frame</span>' +
                '</label>' +
              '</div>' +
              '<div class="swiper-slide gen:!w-auto">' +
                '<label class="variant-option__button-label variant-option__button-label--no-swatch  gen:!border-[2px]  gen:!py-[11px] gen:min-[560px]:!py-[10px] gen:!px-[12px] ">' +
                  '<input type="radio" name="' + nameAttr + '" value="Frame" aria-label="Frame" data-wc-action="select-canvas-frame-mode-d" data-wc-frame-mode="frame">' +
                  '<span class="variant-option__button-label__text gen:min-[560px]:!text-[15px]">Frame</span>' +
                '</label>' +
              '</div>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</fieldset>'
  );
}

/* 2026-05-25 v4 — Upsell now mirrors the EXISTING PBN DIY Frame upsell
   markup byte-for-byte (snippets/wolden-customize-modal.liquid:631-646),
   INCLUDING the <label> + hidden <input type="checkbox"> pattern. This means:
     - The card stays VISIBLE in BOTH canvas and canvas_framed modes (it's
       hidden ONLY on paint_kit). When customer is in canvas_framed mode,
       the checkbox is shown as CHECKED (visible feedback that "Add a frame"
       was applied) — same UX as the PBN DIY Frame upsell.
     - Click toggles the checkbox NATIVELY (browser default), then our
       capture-phase _wcDOnClick reads the post-click checked state to flip
       R.activeFormat to canvas_framed (checked) or canvas (unchecked).
     - _wcDApplyState bidirectionally syncs the checkbox to R.activeFormat
       so the explicit No Frame / Frame pills also keep the checkbox in
       sync (and vice versa).
   The 40×40 <img> from PBN is swapped for an emoji 🖼️ inside the same
   44×44 .wolden-customize__diy-frame-image chip (WC_D_STYLES sizes the
   emoji to fill the chip). Strike + sale prices are data-wired so
   _wcDApplyState can re-key them on size change. */
/* 2026-07-02 QA round-3 — NO static price/savings claims in the markup. The
   badge and the struck/sale numbers start hidden/empty; _wcDApplyState step 6
   fills them (or leaves them hidden) from the real per-size delta. */
function _wcDBuildUpsellCard() {
  return (
    '<label class="wolden-customize__diy-frame-upsell wc-d-upsell-label" data-wc-d-upsell aria-label="Add a frame upsell">' +
      '<input type="checkbox" class="wolden-customize__diy-frame-checkbox" data-wc-action="select-canvas-frame-upsell-d" data-wc-d-upsell-checkbox>' +
      '<span class="wolden-customize__diy-frame-content">' +
        '<span class="wolden-customize__diy-frame-image wc-d-upsell-image" aria-hidden="true">🖼️</span>' +
        '<span class="wolden-customize__diy-frame-text">' +
          '<span class="wolden-customize__diy-frame-title">' +
            'Add a frame' +
            '<span class="wolden-customize__diy-frame-savings" data-wc-d-upsell-badge hidden></span>' +
          '</span>' +
          '<span class="wolden-customize__diy-frame-desc">Premium pre-framed canvas.' +
            '<span data-wc-d-upsell-pricing hidden> <s data-wc-d-upsell-strike></s> <strong data-wc-d-upsell-price></strong> when added today.</span>' +
          '</span>' +
        '</span>' +
      '</span>' +
    '</label>'
  );
}

/* 2026-05-25 v2 — Inject Version D controls into the canvas branch of a "host"
   (the customize modal OR the pdp-options block). Three injections:
     1. Frame fieldset — NEW selector, no analog in Version A (which used format
        tabs). Inserted right after the canvas Size fieldset.
     2. Color fieldset — REUSE the existing canvas_framed branch's
        [data-option-name="Frame Color"] fieldset (Version A's swatch picker).
        Move it into the canvas branch right after the Frame fieldset so the
        canonical select-canvas-framed-color handler keeps driving state.
     3. Upsell card — placed BETWEEN the price line and the Add-to-Cart button
        (modal) or at the end of the canvas branch (pdp-options, where the
        ATC lives in a separate Horizon container above).
   The size inputs in the canvas branch get their action renamed from
   select-canvas-size → select-canvas-size-d so OUR delegator can sync
   activeCanvasSize → activeCanvasFramedSize on every size change. */
/* 2026-05-25 v2 — Pick the SELECTOR branch (the one containing fieldsets), not
   any of the other elements that share data-wc-format-branch="<format>" (the
   slider deck, the card-label paragraph, the bullets list — all four exist in
   the modal). We do this by walking matches and returning the one with a
   variant-option fieldset inside. */
function _wcDFindSelectorBranch(host, format, requireFieldsetOptionName) {
  if (!host) return null;
  var nodes = host.querySelectorAll('[data-wc-format-branch="' + format + '"]');
  for (var i = 0; i < nodes.length; i++) {
    var fld = requireFieldsetOptionName
      ? nodes[i].querySelector('fieldset[data-option-name="' + requireFieldsetOptionName + '"]')
      : nodes[i].querySelector('fieldset.variant-option');
    if (fld) return nodes[i];
  }
  return null;
}

function _wcDInjectControls(host, opts) {
  if (!host) return false;
  opts = opts || {};
  var surface       = opts.surface || 'modal';           // 'modal' | 'pdp'
  var frameNameAttr = opts.frameRadioName || ('wc-' + surface + '-canvas-frame-mode-d');
  // Canvas selector branch = the one with a Size fieldset.
  // Canvas_framed selector branch = the one with a Frame Color fieldset.
  var canvasBranch = _wcDFindSelectorBranch(host, 'canvas', 'Size');
  var framedBranch = _wcDFindSelectorBranch(host, 'canvas_framed', 'Frame Color');
  if (!canvasBranch) return false;

  // Rename canvas-branch size action so our -d delegator fires + can sync.
  var sizeFieldset = canvasBranch.querySelector('fieldset[data-option-name="Size"]');
  if (!sizeFieldset) return false;
  sizeFieldset.querySelectorAll('input[data-wc-action="select-canvas-size"]').forEach(function (input) {
    input.setAttribute('data-wc-action', 'select-canvas-size-d');
  });

  /* 2026-05-25 v3 — IDEMPOTENT Frame insert. Without this guard, every re-run
     of _wcDInjectControls (renderVersionD calls it on every wolden:track event
     for self-healing of the upsell) appends ANOTHER Frame fieldset. The size-
     action rename above is self-idempotent (querySelectorAll returns nothing on
     the second pass once inputs have been renamed) and the colour relocation
     below is self-idempotent (the source fieldset has moved). The Frame insert
     is the ONLY operation that needs an explicit "already-injected" guard. */
  var frameFieldset = canvasBranch.querySelector('fieldset[data-wc-d-row="frame"]');
  if (!frameFieldset) {
    sizeFieldset.insertAdjacentHTML('afterend', _wcDBuildFrameRow(frameNameAttr));
    frameFieldset = canvasBranch.querySelector('fieldset[data-wc-d-row="frame"]');
  }

  // Relocate the EXISTING Version A color fieldset (no reinvention).
  // It lives inside the canvas_framed branch — pull it out, place it in the
  // canvas branch right after the Frame fieldset, mark it as a -d-row, and
  // start it hidden. _wcDApplyState toggles visibility based on Frame mode.
  if (framedBranch && frameFieldset) {
    var colorFieldset = framedBranch.querySelector('fieldset[data-option-name="Frame Color"]');
    if (colorFieldset) {
      colorFieldset.setAttribute('data-wc-d-row', 'color');
      colorFieldset.setAttribute('data-wc-d-relocated', '1');
      colorFieldset.hidden = true;
      frameFieldset.insertAdjacentElement('afterend', colorFieldset);
    }
  }

  /* Upsell card — modal + FLOW reveal surfaces. 2026-05-25 v5 — Per UX
     directive, the plain PDP surface should NOT show the "Add a frame"
     upsell for any format: on a child PDP the ATC lives in the parent
     Horizon product form OUTSIDE pdp-options, so the upsell would be
     visually orphaned and confusing.
     2026-07-02 (apples-to-apples residuals — HANDOFF scope corrections):
     the flow-b / flow-c REVEAL surfaces are the test arms' analog of the
     champion modal reveal, so the SAME card renders there (opts.upsell,
     set by renderVersionDOnPdpOptions for non-standalone flow copies).
     Same trigger conditions + behavior for free: visibility/checkbox/price
     sync live in _wcDApplyState and the click handler in _wcDOnClick, both
     of which query the card per host — no fork. The standalone watercolor
     page stays upsell-free (outside the test; its reference design has
     none). The remove-existing line runs UNCONDITIONALLY so any prior
     bundle's stray upsell gets cleaned up before re-insert. */
  Array.from(host.querySelectorAll('[data-wc-d-upsell]')).forEach(function (u) { u.remove(); });
  if (surface === 'modal') {
    var upsellHtml = _wcDBuildUpsellCard();
    var atcWrap = host.querySelector('.wolden-customize__result-actions--checkout');
    if (atcWrap) {
      atcWrap.insertAdjacentHTML('beforebegin', upsellHtml);
    } else {
      var pricing = host.querySelector('.wolden-customize__pricing');
      if (pricing) pricing.insertAdjacentHTML('afterend', upsellHtml);
      else canvasBranch.insertAdjacentHTML('beforeend', upsellHtml);
    }
  } else if (opts.upsell) {
    /* Flow reveal surfaces: the on-page price line + ATC live outside
       pdp-options (native PDP elements below the flow snippet), so the
       closest analog of the modal's "between price line and ATC" slot is
       the end of the canvas branch — directly under the Frame / Frame
       Color rows the card toggles. WC_D_STYLES adds the on-page margin. */
    canvasBranch.insertAdjacentHTML('beforeend', _wcDBuildUpsellCard());
  }

  return true;
}

function _wcDRefreshChain() {
  var wc = (typeof window !== 'undefined') ? window.WC : null;
  if (!wc) return;
  try { if (typeof wc.refreshActiveBranches === 'function') wc.refreshActiveBranches(); } catch (e) {}
  try { if (typeof wc.refreshSizeFramePills === 'function') wc.refreshSizeFramePills(); } catch (e) {}
  try { if (typeof wc.refreshMockupImages   === 'function') wc.refreshMockupImages();   } catch (e) {}
  try { if (typeof wc.refreshPricingDisplay === 'function') wc.refreshPricingDisplay(); } catch (e) {}
  try { if (typeof wc.updatePdpVariantState === 'function') wc.updatePdpVariantState(); } catch (e) {}
  try { if (typeof wc.refreshAtcReadyState  === 'function') wc.refreshAtcReadyState();  } catch (e) {}
  try { if (typeof wc.renderUnifiedGallery  === 'function') wc.renderUnifiedGallery();  } catch (e) {}
  // CRITICAL for color/size changes in framed mode: swaps the front + context
  // mockup image SRCs to match activeCanvasFramedSize + activeCanvasFramedColor.
  // renderUnifiedGallery alone doesn't re-key those slide images.
  try { if (typeof wc.applyFramedMockupToGallery === 'function') wc.applyFramedMockupToGallery(); } catch (e) {}
}

function _wcDApplyState(host, R) {
  if (!host || !R) return;
  /* 2026-05-26 — Call refreshActiveBranches FIRST so every data-wc-format-
     branch element (including the PBN .wolden-customize__diy-frame-upsell
     label that lives between pricing and ATC) gets its hidden state synced
     to R.activeFormat. Without this, _wcDApplyState's selector-branch
     overrides ran in isolation, and any sibling data-wc-format-branch node
     (most importantly the DIY Frame upsell on Paint Kit) could keep a stale
     hidden state — making its <img loading="lazy"> never enter the viewport
     and never load. Our subsequent overrides in step 1 below ONLY toggle
     the three selector branches, so the canonical sync below stays correct
     for all the sibling elements. */
  /* 2026-07-02 (flow-picker rework) — SKIP the canonical sync while inside
     the rab-wrapper's re-apply pass (__inVdReapply). With multiple injected
     pdp-options copies on the page (current/flow-b/flow-c co-reside until
     wc77-init's subtree removal — and the modal is always a second host),
     each re-applied host's canonical call here re-stomped the siblings the
     wrapper had ALREADY healed earlier in its loop, so only the last host
     ended up consistent. During a re-apply the canonical sync this call
     exists for (sibling data-wc-format-branch nodes like the DIY upsell)
     has just run — it's what triggered the wrapper — so skipping is safe. */
  try {
    var wcSel0 = (typeof window !== 'undefined' && window.WC) ? window.WC : {};
    if (!wcSel0.__inVdReapply && typeof wcSel0.refreshActiveBranches === 'function') wcSel0.refreshActiveBranches();
  } catch (_) {}
  /* 2026-05-25 v2 — surface-agnostic. `host` may be either the customize modal
     or the pdp-options root. Both have the same branch markup (data-wc-format-
     branch="canvas"|"canvas_framed"|"paint_kit"); we resolve to the SELECTOR
     branch (the one containing the variant-option fieldsets) so we don't
     accidentally toggle hidden on the card-label/slider-deck/bullets that
     also share the data-wc-format-branch attribute in the modal. */
  var canvasBranch = _wcDFindSelectorBranch(host, 'canvas',        'Size');
  var framedBranch = _wcDFindSelectorBranch(host, 'canvas_framed', null);
  var pkBranch     = _wcDFindSelectorBranch(host, 'paint_kit',     null);
  var inFramedMode = R.activeFormat === 'canvas_framed';

  // 1. Right-column branch visibility — override the existing refreshActiveBranches
  //    behavior so the Canvas panel stays visible while activeFormat flips
  //    between canvas and canvas_framed under the hood. The LEFT slider decks
  //    (modal only) are NOT touched here — they continue to follow activeFormat
  //    strictly so the gallery shows the right mockup.
  if (R.activeFormat === 'paint_kit') {
    if (canvasBranch) canvasBranch.hidden = true;
    if (framedBranch) framedBranch.hidden = true;
    if (pkBranch)     pkBranch.hidden     = false;
  } else {
    if (canvasBranch) canvasBranch.hidden = false;
    if (framedBranch) framedBranch.hidden = true; // suppress entirely — its Color fieldset was relocated
    if (pkBranch)     pkBranch.hidden     = true;
  }

  // 2. Frame-mode pills active state + display.
  //    Drive via the canonical _setSelected helper (radio.checked +
  //    label.is-checked + inline-style paint) so the active pill looks
  //    visually IDENTICAL to the Paint Kit Frame pill on the same surface.
  var wcSel = (typeof window !== 'undefined' && window.WC) ? window.WC : {};
  var setSel = typeof wcSel._setSelected === 'function' ? wcSel._setSelected : null;
  host.querySelectorAll('input[data-wc-action="select-canvas-frame-mode-d"]').forEach(function (input) {
    var mode = input.getAttribute('data-wc-frame-mode');
    var on = (mode === 'frame') === inFramedMode;
    if (setSel) setSel(input, on);
    else { input.checked = on; } // graceful fallback if _setSelected isn't attached yet
  });
  var frameDisplay = host.querySelector('[data-wc-d-frame-display]');
  if (frameDisplay) frameDisplay.textContent = inFramedMode ? 'Frame' : 'No Frame';

  /* 2b. Canvas-size pill active state. 2026-05-25 v5 — Without this loop the
     button visual lagged behind the radio.checked state on every Size click.
     Root cause: the existing refreshSizeFramePills paints labels for radios
     whose data-wc-action is `select-canvas-size`, but Version D renames those
     to `select-canvas-size-d` so the helper never finds them. We mirror the
     paint here via the same _setSelected helper. Active = the radio whose
     data-wc-size matches R.activeCanvasSize (the customer's current pick). */
  /* 2026-07-02 — paint ONLY once R actually carries a size. A pre-hydration
     apply (the 50ms pdp bootstrap can beat both the child-PDP hydrator and
     the flow modules' seedPickerState) used to force the 8x10 default onto
     the pills, clobbering the server-rendered URL-derived checked state that
     the flow modules seed R FROM (e.g. a shared /custom-canvas-print-16x20-…
     link). Leaving the Liquid state alone until R is seeded is always at
     least as correct. */
  if (R.activeCanvasSize) {
    var activeCanvasSize = R.activeCanvasSize;
    host.querySelectorAll('input[data-wc-action="select-canvas-size-d"]').forEach(function (input) {
      var on = input.getAttribute('data-wc-size') === activeCanvasSize;
      if (setSel) setSel(input, on);
      else { input.checked = on; }
    });
  }

  /* 2c. PDP format radios + "Format :" legend. 2026-07-02 (flow-picker
     rework) — the pdp-options surfaces select format via <input type=radio>
     (the modal uses button tiles — step 8 below). Under Version D the
     Canvas Print radio represents BOTH canvas and canvas_framed (the frame
     choice lives in the nested Frame row), but the canonical
     refreshActiveBranches unchecks EVERY format radio when activeFormat ===
     'canvas_framed' (no radio carries that value) and flips the legend to
     its else-branch label. Re-paint here with the same rule step 8 uses for
     the modal tiles. Skipped until R carries a format so a pre-hydration
     apply can't uncheck the server-rendered default. */
  if (R.activeFormat) {
    var canvasFmtActive = (R.activeFormat === 'canvas' || R.activeFormat === 'canvas_framed');
    host.querySelectorAll('input[data-wc-action="select-format"]').forEach(function (input) {
      var fmt = input.getAttribute('data-wc-format');
      var onFmt = (fmt === 'canvas') ? canvasFmtActive : (fmt === R.activeFormat);
      if (setSel) setSel(input, onFmt);
      else { input.checked = onFmt; }
    });
    var fmtDisplay = host.querySelector('[data-wc-element="format-display"]');
    if (fmtDisplay) fmtDisplay.textContent = canvasFmtActive ? 'Canvas Print' : 'Paint Kit';
  }

  // 3. Color row visibility — visible only in framed mode. The fieldset was
  //    relocated from canvas_framed branch into canvas branch by _wcDInjectControls.
  var colorRow = host.querySelector('fieldset[data-wc-d-row="color"]');
  if (colorRow) colorRow.hidden = !inFramedMode;

  /* 4. Frame Color pill active state. 2026-05-27 — Earlier comment claimed
     the CANONICAL refresh chain (refreshSizeFramePills) would paint these
     radios. Live test on wc64 showed that's FALSE for the relocated fieldset:
     state mutates to White correctly + gallery swaps to white mockup, but the
     radio.checked stays on Black so the active-pill ring sticks to the old
     color. Root cause is the same shape as the size pill bug we fixed earlier:
     refreshSizeFramePills uses scoped selectors that expect the original
     canvas_framed branch layout, and the relocated fieldset's host branch is
     different. Mirror the size paint here via the same _setSelected helper so
     the visual stays in sync with R.activeCanvasFramedColor. Active = the
     radio whose data-wc-color matches the customer's current pick (default
     Black per Version D's first-Frame-entry contract). */
  /* 2026-07-02 — same pre-hydration guard as the size pills above: don't
     force the Black default over a server-rendered URL-derived color (framed
     child deep links) before R has been seeded. */
  if (R.activeCanvasFramedColor) {
    var activeFramedColor = R.activeCanvasFramedColor;
    host.querySelectorAll('input[data-wc-action="select-canvas-framed-color"]').forEach(function (input) {
      var on = input.getAttribute('data-wc-color') === activeFramedColor;
      if (setSel) setSel(input, on);
      else { input.checked = on; }
    });
  }

  /* 5. Upsell card visibility + checkbox sync.
     2026-05-25 v4 — Mirror PBN DIY Frame upsell behavior: upsell stays
     VISIBLE in both canvas AND canvas_framed modes; the checkbox shows
     CHECKED when in canvas_framed (visible confirmation that "Add a frame"
     was applied). Hidden ONLY on paint_kit (which has its own DIY Frame
     upsell — both wouldn't make sense on the same panel).

     Bidirectional sync: when the customer clicks the No Frame / Frame
     pills, we re-check/uncheck the upsell's checkbox here. When the
     customer clicks the upsell, the click handler reads target.checked
     and flips R.activeFormat accordingly. */
  var upsell = host.querySelector('[data-wc-d-upsell]');
  if (upsell) upsell.hidden = (R.activeFormat === 'paint_kit');
  var upsellCheckbox = host.querySelector('[data-wc-d-upsell-checkbox]');
  if (upsellCheckbox) {
    var shouldCheck = (R.activeFormat === 'canvas_framed');
    if (upsellCheckbox.checked !== shouldCheck) upsellCheckbox.checked = shouldCheck;
  }

  /* 6. Upsell pricing — HONEST per-size delta (2026-07-02 QA round-3).
     The struck/sale numbers are computed from the same price tables the
     price line uses (see _wcDComputeUpsellDelta): sale = what adding the
     frame actually costs at this size today, strike = the compare-at delta.
     Strike + SAVE badge render only when the strike is genuinely larger;
     with no pricing available NO numbers render (never a made-up claim).
     Runs per host, so the modal and the flow reveals behave identically. */
  var size = R.activeCanvasSize || '8x10';
  var delta = _wcDComputeUpsellDelta(size);
  var priceEl   = host.querySelector('[data-wc-d-upsell-price]');
  var strikeEl  = host.querySelector('[data-wc-d-upsell-strike]');
  var pricingEl = host.querySelector('[data-wc-d-upsell-pricing]');
  var badgeEl   = host.querySelector('[data-wc-d-upsell-badge]');
  if (delta) {
    if (priceEl)  priceEl.textContent = _wcDFmtDelta(delta.sale);
    if (strikeEl) {
      strikeEl.textContent = delta.strike ? _wcDFmtDelta(delta.strike) : '';
      strikeEl.hidden = !delta.strike;
    }
    if (pricingEl) pricingEl.hidden = false;
    if (badgeEl) {
      if (delta.pct) {
        badgeEl.textContent = 'SAVE ' + delta.pct + '%';
        badgeEl.setAttribute('aria-label', 'Save ' + delta.pct + ' percent');
        badgeEl.hidden = false;
      } else {
        badgeEl.textContent = '';
        badgeEl.removeAttribute('aria-label');
        badgeEl.hidden = true;
      }
    }
  } else {
    // No real numbers → no claims. The card still works as a frame toggle.
    if (pricingEl) pricingEl.hidden = true;
    if (badgeEl) { badgeEl.textContent = ''; badgeEl.removeAttribute('aria-label'); badgeEl.hidden = true; }
  }

  // 7. LEFT slider deck visibility (modal only — pdp-options has no slider
  //    deck, just the variant selectors). Safety net so the gallery switches
  //    between canvas / canvas_framed / paint_kit decks regardless of whether
  //    the upstream refreshActiveBranches reached this branch (e.g., during
  //    early ticks before window.WC.refreshActiveBranches has been attached).
  var canvasDeck = host.querySelector('[data-wc-format-branch="canvas"].wolden-customize__slider-deck');
  var framedDeck = host.querySelector('[data-wc-format-branch="canvas_framed"].wolden-customize__slider-deck');
  var pkDeck     = host.querySelector('[data-wc-format-branch="paint_kit"].wolden-customize__slider-deck');
  if (canvasDeck) canvasDeck.hidden = R.activeFormat !== 'canvas';
  if (framedDeck) framedDeck.hidden = R.activeFormat !== 'canvas_framed';
  if (pkDeck)     pkDeck.hidden     = R.activeFormat !== 'paint_kit';

  /* 8. Format-tile "active" border override. 2026-05-25 v4 — In Version D the
     Canvas Print tab represents BOTH the unframed and framed canvas product
     states (the customer flips between them via the Frame fieldset / upsell).
     The existing format-tab JS toggles the .wolden-customize__format-tile--
     active class based on the radio whose data-wc-format matches activeFormat.
     When activeFormat === 'canvas_framed', no Version D tab matches (there's
     no Framed Canvas tab in Version D — only Canvas Print + Paint Kit), so
     the Canvas Print tile loses its --active border. Per UX guidance (don't
     mess with the data layer — the variant lookup needs activeFormat = canvas
     _framed): we paint the Canvas Print tile as --active whenever activeFormat
     is canvas OR canvas_framed. Paint Kit tile stays --active only on
     paint_kit. */
  var canvasIsActive = (R.activeFormat === 'canvas' || R.activeFormat === 'canvas_framed');
  host.querySelectorAll('.wolden-customize__format-tile[data-wc-format="canvas"]').forEach(function (tile) {
    tile.classList.toggle('wolden-customize__format-tile--active', canvasIsActive);
  });
  host.querySelectorAll('.wolden-customize__format-tile[data-wc-format="paint_kit"]').forEach(function (tile) {
    tile.classList.toggle('wolden-customize__format-tile--active', R.activeFormat === 'paint_kit');
  });
}

function _wcDOnClick(host, e) {
  var target = e.target && e.target.closest && e.target.closest('[data-wc-action]');
  if (!target || !host.contains(target)) return;
  var action = target.getAttribute('data-wc-action');
  /* 2026-05-25 v2 — we ONLY intercept the three -d actions Version D OWNS:
     size-d (so we can sync activeCanvasFramedSize), frame-mode-d (the new
     toggle), and frame-upsell-d (the promo card). The COLOR selector now uses
     the canonical `select-canvas-framed-color` action — the existing handler
     in wc50-selectors.js owns it, including refreshMockupImages, gallery
     re-render, and the variant-id update. No -d color action needed. */
  if (action !== 'select-canvas-size-d' &&
      action !== 'select-canvas-frame-mode-d' &&
      action !== 'select-canvas-frame-upsell-d') return;
  /* 2026-05-25 — Do NOT call e.preventDefault() on radio clicks. Per HTML spec
     activation steps, when a radio click is canceled the browser ROLLS BACK
     the pre-click activation (restores the prior radio.checked state) AFTER
     all listeners run. That would undo our _setSelected paint and leave the
     just-clicked radio unchecked while the label shows .is-checked — a
     visually-correct-but-functionally-broken state. The existing handlers
     work around this with syncRadioGroupBy + microtask; for our -d delegator
     it's simpler to just let the browser's native radio toggle happen. */
  var wc = (typeof window !== 'undefined') ? (window.WC || {}) : {};
  var R  = wc._currentResult || {};
  /* 2026-05-27 — Surface detection. The customize modal has
     data-wc-component="modal"; the PDP-options host (#pdp-options) does not.
     The original canonical handlers split into PDP vs modal versions with
     different side-effect timing (immediate cleanBreakNow on PDP, deferred
     on modal); Version D must do the same so the customer's URL/handle
     swap behavior matches no matter which surface they touched. */
  var isPdpSurface    = host.getAttribute('data-wc-component') !== 'modal';
  var canonicalSource = isPdpSurface ? 'pdp' : 'modal';

  // ── Size pill (synced, format-aware) ────────────────────────────────────
  if (action === 'select-canvas-size-d') {
    var size = target.getAttribute('data-wc-size');
    if (!size) return;
    /* 2026-05-27 — captureFramedSlotIntent BEFORE state mutation. Mirrors
       the canonical select-canvas-size + select-canvas-framed-size handlers
       (wc50-selectors.js lines 1731/1861/2295/2623). Without this, the
       updatePdpVariantState call below fires Horizon's variant:changed event
       which resets <slideshow-component>.current to slot 0 (art-only),
       hiding the just-keyed framed mockup at slot 1. THIS is the root cause
       of the reported "gallery image does not change on size click in Frame
       mode" bug. */
    try { if (typeof wc.captureFramedSlotIntent === 'function') wc.captureFramedSlotIntent(); } catch (_) {}
    /* 2026-05-27 — Branch on activeFormat. The canonical no-frame handler
       (select-canvas-size) mutates activeCanvasSize and the framed handler
       (select-canvas-framed-size) mutates activeCanvasFramedSize. Each calls
       a DIFFERENT clean-break reason and tracks a different format value.
       Version D needs to dispatch to the right pair based on the current
       mode — same logical handler, different state field + reason string. */
    var inFramedMode = R.activeFormat === 'canvas_framed';
    if (inFramedMode) {
      if (size === R.activeCanvasFramedSize) return;
      R.activeCanvasFramedSize = size;
      R.activeCanvasSize       = size; // keep linked so toggle back to No-Frame preserves size
    } else {
      if (size === R.activeCanvasSize) return;
      R.activeCanvasSize       = size;
      R.activeCanvasFramedSize = size;
    }
    _wcDRefreshChain();
    _wcDApplyState(host, R);
    /* 2026-05-27 — Queue the URL/title/handle swap to the new size's child
       product. Canonical handlers: applyCleanBreakNow on PDP surface (immediate
       handle/URL swap so a refresh lands on the right product); applyCleanBreakDeferred
       on modal surface (queued for flush on modal close so per-click work
       doesn't freeze the modal). Without these, Version D mutates state but
       the customer's URL never matches their selection — reload breaks. */
    var sizeReason = inFramedMode
      ? 'wc-d-' + canonicalSource + '-canvas-framed-size-change'
      : 'wc-d-' + canonicalSource + '-canvas-size-change';
    try {
      if (isPdpSurface && typeof wc.applyCleanBreakNow === 'function') {
        wc.applyCleanBreakNow(sizeReason);
      } else if (typeof wc.applyCleanBreakDeferred === 'function') {
        wc.applyCleanBreakDeferred(sizeReason);
      }
    } catch (_) {}
    try {
      track('size_selected', {
        generation_id: R.generationId,
        format: R.activeFormat,
        size: size,
        source: canonicalSource,
      });
    } catch (_) {}
    return;
  }

  // ── Frame-mode toggle OR Upsell-card click ──────────────────────────────
  if (action === 'select-canvas-frame-mode-d' || action === 'select-canvas-frame-upsell-d') {
    /* 2026-05-25 v4 — Upsell is now a <label>+<input type="checkbox"> (mirror
       of PBN DIY Frame upsell). The browser toggles the checkbox BEFORE the
       click event reaches us, so by reading target.checked we know what the
       customer wants:
         - upsell checked   → switch to Frame mode (canvas_framed)
         - upsell unchecked → switch back to No Frame mode (canvas)
       Frame-mode pill clicks (the other branch) carry their target mode in
       data-wc-frame-mode as before. */
    var mode;
    if (action === 'select-canvas-frame-upsell-d') {
      mode = target.checked ? 'frame' : 'no-frame';
    } else {
      mode = target.getAttribute('data-wc-frame-mode');
    }
    if (!mode) return;
    var nextFormat = (mode === 'frame') ? 'canvas_framed' : 'canvas';
    if (R.activeFormat !== nextFormat) {
      /* 2026-05-27 — Capture slot BEFORE mutation (same reason as size handler). */
      try { if (typeof wc.captureFramedSlotIntent === 'function') wc.captureFramedSlotIntent(); } catch (_) {}
      R.activeFormat = nextFormat;
      /* 2026-05-27 — Dispatch wc:format-changed so the reviews slider and any
         other format-reactive component reacts. The canonical select-format
         handler fires this event; Version D's relocated frame-mode pill must
         dispatch the same event so downstream listeners stay in sync. */
      try {
        document.dispatchEvent(new CustomEvent('wc:format-changed', {
          detail: { format: nextFormat, source: 'wc-d-' + canonicalSource }
        }));
      } catch (_) {}
      /* 2026-05-27 — Call canonical per-format reset for cross-format state
         fields. The manual seeding below only covers framed size + framed
         color. resetFormatDefaults also touches PBN cross-format fields. */
      try { if (typeof wc.resetFormatDefaults === 'function') wc.resetFormatDefaults(R, nextFormat); } catch (_) {}
      if (nextFormat === 'canvas_framed') {
        /* 2026-05-25 v2 — Existing manual seeding (kept): On EVERY Frame-mode
           entry, sync the framed size to whatever canvas size the customer
           last picked (so 8x10 / 16x20 / 24x30 carries over). The native
           Liquid template defaults canvas_framed color to 'White'; the UX
           spec for Version D is Black on first entry. Sentinel preserves
           customer's last-picked color on SUBSEQUENT toggles. Runs AFTER
           resetFormatDefaults so the Black sentinel persists. */
        R.activeCanvasFramedSize = R.activeCanvasSize || R.activeCanvasFramedSize || '8x10';
        if (!R._wcDFrameSeeded) {
          R._wcDFrameSeeded = true;
          R.activeCanvasFramedColor = 'Black';
        }
        if (!R.activeCanvasFramedColor) R.activeCanvasFramedColor = 'Black';
      } else {
        /* 2026-07-02 QA round-1 fix — MIRROR re-seed on EXIT to No-Frame.
           resetFormatDefaults(R,'canvas') above just forced activeCanvasSize
           back to '8x10'; without restoring it from the framed pick, the
           16x20→Frame→No-Frame path silently downgraded the customer to
           8x10/$47.90 while the framed group still held 16x20 (wrong size
           ordered, or lost upsell on the way back). The size handler keeps
           the two fields linked on every size click, so the framed size IS
           the customer's last pick. Entry direction already re-seeds above. */
        R.activeCanvasSize = R.activeCanvasFramedSize || R.activeCanvasSize || '8x10';
      }
      _wcDRefreshChain();
      /* 2026-05-25 v2 — Auto-jump the framed slider deck to slide #2 (index 1)
         on EVERY entry into Frame mode. Slot 0 = finished art, slot 1 = front
         mockup at active size+color, slot 2 = context mockup. The customer
         should see "Premium Framed Canvas" as the front mockup immediately. */
      try {
        if (nextFormat === 'canvas_framed' && wc && typeof wc.setSlideIndex === 'function') {
          wc.setSlideIndex('canvas_framed', 1);
        }
      } catch (_) {}
      /* 2026-05-27 — Queue URL/handle swap to the new format's child product
         (canvas vs canvas_framed). Same surface routing as size handler. */
      var modeReason = 'wc-d-' + canonicalSource + '-frame-mode-change';
      try {
        if (isPdpSurface && typeof wc.applyCleanBreakNow === 'function') {
          wc.applyCleanBreakNow(modeReason);
        } else if (typeof wc.applyCleanBreakDeferred === 'function') {
          wc.applyCleanBreakDeferred(modeReason);
        }
      } catch (_) {}
      /* 2026-05-27 — Cross-fade gallery for format change. Canonical
         select-format wraps renderUnifiedGallery in this ~400ms transition
         so the format swap feels smooth; without it Version D produces a
         jarring instant cut. */
      try {
        if (typeof wc.transitionGalleryForFormatChange === 'function') {
          wc.transitionGalleryForFormatChange(function () {
            try { if (typeof wc.renderUnifiedGallery === 'function') wc.renderUnifiedGallery(); } catch (_) {}
          });
        }
      } catch (_) {}
      try {
        track('format_toggled', {
          generation_id: R.generationId,
          format: nextFormat,
          source: canonicalSource,
        });
      } catch (_) {}
    }
    _wcDApplyState(host, R);
    return;
  }
}

function _wcDInstallDelegator(host) {
  if (host.__wcDDelegatorInstalled) return;
  host.__wcDDelegatorInstalled = true;
  // Capture phase so we run BEFORE the existing host click delegator. Our
  // actions (-d suffix) are unique, so we won't intercept anything else.
  host.addEventListener('click', function (e) {
    try { _wcDOnClick(host, e); } catch (_) {}
  }, true);
  // After ANY format change (e.g., Paint Kit tile clicked), the existing
  // select-format handler fires its `track('format_toggled', ...)` AFTER
  // calling refreshActiveBranches. Listening on the wolden:track event
  // (which the analytics module dispatches) means we re-apply Version D's
  // visibility AFTER the existing refresh has run, so our override wins.
  /* 2026-05-27 — Re-apply Version D state on EVERY canonical selector event
     (format_toggled, size_selected, frame_color_selected) — not just
     format_toggled. The relocated color fieldset still uses the canonical
     select-canvas-framed-color action (handled by wc50-selectors.js, not
     our -d delegator); without re-applying after that event, Version D's
     deck-visibility / pill-paint / upsell-checkbox overrides stay stale if
     Horizon's variant:changed coincidentally touches a sibling node.
     Size-selected coverage is defense-in-depth for the same reason. The
     callback is idempotent (_wcDApplyState is safe to re-run). */
  document.addEventListener('wolden:track', function (ev) {
    try {
      var name = ev && ev.detail && ev.detail.eventName;
      if (name === 'format_toggled' ||
          name === 'size_selected' ||
          name === 'frame_color_selected') {
        var wc = (typeof window !== 'undefined') ? (window.WC || {}) : {};
        _wcDApplyState(host, wc._currentResult || {});
      }
    } catch (_) {}
  });
}

function renderVersionD(modalEl, R) {
  try {
    if (!modalEl) return;
    _wcDEnsureStyles();
    modalEl.setAttribute('data-wc-exp', 'version_d');
    var canvasBranch = modalEl.querySelector('[data-wc-format-branch="canvas"].wolden-customize__format-branch');
    if (!canvasBranch) return;
    // 2026-05-25 v2 — Re-run injection EVERY call. _wcDInjectControls is
    // self-healing (drops any stray upsell + re-inserts; relocation is
    // idempotent because the source fieldset moves once and queries return
    // null on re-call). This handles modal renderings where the result
    // section's ATC wrapper wasn't in DOM on the first call but is on later
    // calls — without the re-run, the upsell sticks in its fallback spot.
    _wcDInjectControls(modalEl, { surface: 'modal' });
    if (!modalEl.__wcDDelegatorInstalled) _wcDInstallDelegator(modalEl);
    modalEl.__wcDInjected = true;
    _wcDApplyState(modalEl, R || {});
    // Also drive the PDP-options surface in the same pass — the child PDP
    // shares the same activeFormat / activeCanvasSize / activeCanvasFramedColor
    // state, so Version D should look the same in both places.
    try { renderVersionDOnPdpOptions(R || {}); } catch (_) {}
    // 2026-05-27 — Branch-reapply guard (extracted 2026-07-02 to
    // _wcDEnsureRabWrapper so the pdp-options bootstrap can install it too —
    // flow pages may never render the modal path). See the helper for the
    // full WHY.
    _wcDEnsureRabWrapper();
  } catch (e) {
    // Per applyToReveal contract: never throw — falls back to renderControl.
  }
}

/* 2026-05-27 (extracted 2026-07-02) — Branch-reapply guard. Multiple legacy
   code paths call window.WC.refreshActiveBranches() AFTER Version D has set
   its branch override (e.g., select-canvas-framed-size handler, the upsell-
   checkbox toggle, and the /cart/add.js success cascade via cart:update).
   Each of those resets canvas/canvas_framed/paint_kit hidden attributes back
   to the DEFAULT behavior — which hides the canvas branch (where the
   relocated Frame: + Frame Color: fieldsets live) whenever activeFormat ===
   'canvas_framed'. The wolden:track listener inside _wcDInstallDelegator is
   supposed to catch this but doesn't fire on all of these paths.

   Fix: wrap refreshActiveBranches ONCE so every future call automatically
   re-applies _wcDApplyState. Gated by _expVariant === 'version_d' so the
   wrapper is a no-op for a QA-forced non-D variant. Re-entry guard prevents
   infinite recursion when _wcDApplyState itself calls refreshActiveBranches.

   2026-07-01 — champion-default gate: a null/undefined _expVariant cache
   (possible if a render path reached us before any getActiveVariant call)
   must count as version_d — otherwise the re-apply silently disables itself
   and the 2026-05-27 hidden-branch bug returns.

   2026-07-02 (flow-picker rework) — callable from BOTH render paths (modal
   renderVersionD + renderVersionDOnPdpOptions), and the pdp re-apply
   selector is FIXED: it was '#pdp-options, [data-wc-pdp-options]', which
   matches NOTHING in this theme (every copy carries
   data-wc-component="pdp-options") — the wrapper silently never re-applied
   on the pdp surface. Now all copies (flow arms included) re-apply; the
   __wcDInjected guard keeps un-injected hosts untouched. */
function _wcDEnsureRabWrapper() {
  try {
    var wcRoot = (typeof window !== 'undefined') ? window.WC : null;
    if (!wcRoot || wcRoot.__rabWrappedForVD || typeof wcRoot.refreshActiveBranches !== 'function') return;
    var _origRab = wcRoot.refreshActiveBranches;
    wcRoot.refreshActiveBranches = function _vdRabWrapper() {
      var ret = _origRab.apply(this, arguments);
      if (wcRoot.__inVdReapply) return ret; // skip during recursive _wcDApplyState
      try {
        if ((wcRoot._expVariant == null || wcRoot._expVariant === 'version_d') && wcRoot._currentResult) {
          wcRoot.__inVdReapply = true;
          try {
            var _modal = document.querySelector('[data-wc-component="modal"]');
            if (_modal && _modal.__wcDInjected) _wcDApplyState(_modal, wcRoot._currentResult);
            document.querySelectorAll('[data-wc-component="pdp-options"]').forEach(function (_pdp) {
              if (_pdp.__wcDInjected) _wcDApplyState(_pdp, wcRoot._currentResult);
            });
          } finally {
            wcRoot.__inVdReapply = false;
          }
        }
      } catch (_e) { /* never throw — wrapper must stay transparent */ }
      return ret;
    };
    wcRoot.__rabWrappedForVD = true;
  } catch (_) { /* defensive — wrap is best-effort */ }
}

/* 2026-05-25 v2 — Apply Version D to the PDP-options surface (the child product
   page's variant selector). Same Frame fieldset, same relocated color fieldset,
   same upsell card, same click delegator — driven by the same _wcDApplyState
   call. Safe to call multiple times: _wcDInjectControls is self-healing and
   _wcDInstallDelegator guards against double-wiring. */
export function renderVersionDOnPdpOptions(R) {
  try {
    if (typeof document === 'undefined') return;
    /* 2026-07-02 (flow-picker rework — HANDOFF scope-correction item 2,
       apples-to-apples rule) — the projection now applies to EVERY
       pdp-options copy, the flow-b / flow-c copies INCLUDED. The arms must
       be structurally identical to the current arm's pickers (2 format
       tiles, Frame row + Frame Color nested inside Canvas), and this
       projection IS that structure — the 2026-07-01 flow-b/flow-c skips
       went away with the three-tile layout they protected.
       querySelectorAll because up to three copies co-reside at parse time
       (current / flow-b / flow-c) until wc77-init's subtree removal;
       injecting all of them is idempotent per host and keeps whichever copy
       survives correct regardless of boot ordering. */
    var copies = document.querySelectorAll('[data-wc-component="pdp-options"]');
    if (!copies.length) return;
    /* 2026-07-01 — getActiveVariant now defaults to 'version_d' (baked champion)
       without any PostHog/flag read, so this gate passes for every customer.
       It remains ONLY so a QA ?wc_exp override to another variant correctly
       skips the D projection on the pdp surfaces. */
    var v = getActiveVariant();
    if (v !== 'version_d') return;
    _wcDEnsureStyles();
    var current = R || (window.WC && window.WC._currentResult) || {};
    copies.forEach(function (pdpOptions) {
      var canvasBranch = pdpOptions.querySelector('[data-wc-format-branch="canvas"]');
      if (!canvasBranch) return;
      pdpOptions.setAttribute('data-wc-exp', 'version_d');
      /* Injected Frame radios are name-grouped PER COPY: co-resident copies
         sharing one radio-group name steal each other's checked state (the
         exact first-paint bug b6bb03a fixed for the Liquid-rendered
         radios) — so each flow surface gets its own group name. */
      var frameName = 'wc-pdp-canvas-frame-mode-d';
      var flowRoot = pdpOptions.closest('[data-wc-component="flow-b"], [data-wc-component="flow-c"]');
      if (flowRoot && flowRoot.getAttribute('data-wc-component') === 'flow-b') frameName = 'wc-flowb-canvas-frame-mode-d';
      else if (flowRoot) frameName = 'wc-flowc-canvas-frame-mode-d';
      /* 2026-07-02 (apples-to-apples residuals) — upsell parity: the champion
         modal reveal renders the "Add a frame — SAVE 40%" card, so the flow
         arms' reveal surfaces must too. Standalone is EXCLUDED: it is outside
         the test (no parity requirement) and its reference design has no
         upsell — the data-wc-standalone attribute on the flow-b root is the
         switch. Plain PDP copies (no flow root) keep the 2026-05-25 v5
         no-upsell directive. */
      var flowUpsell = !!flowRoot && !flowRoot.hasAttribute('data-wc-standalone');
      _wcDInjectControls(pdpOptions, { surface: 'pdp', frameRadioName: frameName, upsell: flowUpsell });
      if (!pdpOptions.__wcDDelegatorInstalled) _wcDInstallDelegator(pdpOptions);
      pdpOptions.__wcDInjected = true;
      _wcDApplyState(pdpOptions, current);
    });
    /* Same refreshActiveBranches wrapper the modal path installs — flow
       pages may never run renderVersionD (their reveal is on-page, not the
       modal), and without the wrapper any un-tracked refreshActiveBranches
       call would reset branch visibility out from under the nested-frame
       layout while in Frame mode. */
    _wcDEnsureRabWrapper();
  } catch (_) {
    /* defensive: never throw — pdp-options stays in its native state if anything fails */
  }
}

/* 2026-05-25 v2 — Boot hook for PDP-options.
   Customers landing on a child PDP (e.g. /products/custom-canvas-print-xxx)
   never trigger the modal flow at all on first paint. Without this boot hook,
   Version D would only paint the modal — leaving the cold PDP-options page
   showing the canonical 2-tab format chooser instead of the Frame-row layout.

   We attach via DOMContentLoaded (or fire immediately if past it) AND also
   re-attempt on every wolden:track event, because:
     a) pdp-options is server-rendered, so DOMContentLoaded is the earliest
        safe moment to inject.
     b) state-hydration is async (initPdpOptionsState may fire after our boot
        tick), so we want to re-paint after any tracking event in case R
        has been hydrated since the last apply call. */
function _ensurePdpOptionsVersionD() {
  var run = function () {
    try {
      var R = (typeof window !== 'undefined' && window.WC && window.WC._currentResult) || {};
      renderVersionDOnPdpOptions(R);
    } catch (_) {}
  };
  if (typeof document === 'undefined') return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    // Defer one tick so a later-installed pdp-options init can finish hydrating R.
    setTimeout(run, 50);
  }
  // Re-apply on every tracking event (cheap + idempotent).
  if (typeof document !== 'undefined') {
    document.addEventListener('wolden:track', run);
  }
}

// Self-bootstrap PDP-options. ESM module side-effect: runs once when the
// experiments module first loads. Guarded so multi-imports don't double-wire.
if (typeof window !== 'undefined' && !window.__wcDPdpBootstrapped) {
  window.__wcDPdpBootstrapped = true;
  try { _ensurePdpOptionsVersionD(); } catch (_) {}
}
